/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useBaseService from "../../../../hooks/services/useBaseService";
import useNotification from "../../../../hooks/useNotification";
import useLoader from "../../../../hooks/useLoader";
import useForm from "../../../../hooks/useForm";
import { groupFields } from "../../../../constants/forms/almoxarifado/groupFields";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import SectionHeader from "../../../../components/forms/SectionHeader";
import Form from "../../../../components/forms/Form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MainLayout from "../../../../layouts/MainLayout";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";
import useRemoteSelectOptions from "../../../../hooks/useRemoteSelectOptions";
import { useSectionFields } from "../../../../hooks/useSectionFields";

const EditGroupPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const {
    getByColumn: baseServiceGetByColumn,
    get: baseServiceGet,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { formData, handleChange, formatData, resetForm } = useForm(
    setDefaultFieldValues(groupFields),
    groupFields,
  );

  // --- Query para buscar os detalhes do Grupo ---
  const {
    data: groupData,
    isLoading: isLoadingGroup,
    isError: isErrorGroup,
  } = useQueryWithLoader({
    queryKey: ["group", id],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(
        entities.groups.getById(id),
      );
      return response.result;
    },
  });

  const [fields, setFields] = React.useState(groupFields[0].fields);
  const { updateField } = useSectionFields();

  useEffect(() => {
    let updatefields = [...groupFields[0].fields];
    if (groupData && groupData.is_in_product) {
      updatefields = updateField({
        arrayDependente: updatefields,
        field_id: "group.name",
        updates: { disabled: true },
      });
    }

    setFields(updatefields);
  });

  // Hook para buscar os tipos no servidor
  const typeSelect = useRemoteSelectOptions({
    queryKey: ["typeOptions"],
    entityPath: entities.types.get,
    baseServiceGet,
    fieldId: "group.types",
    formData,
    immediateFetch: true,
    isMulti: true,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (type) => ({ value: type.id, label: type.name }),
  });

  const selects = {
    "group.types": typeSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};
      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  useEffect(() => {
    if (!isLoadingGroup && !isErrorGroup && groupData) {
      // Preparamos o objeto exatamente como o formatData espera encontrar as categorias
      const dataToFormat = {
        group: {
          ...groupData,
          // Transformamos os tipos em objetos {value, label} antes do formatData
          types:
            groupData.types?.map((t) => ({
              value: t.id,
              label: t.name,
            })) || [],
        },
      };

      formatData(dataToFormat, groupFields);
      hideLoader();
    }
  }, [isLoadingGroup, isErrorGroup, groupData, formatData, hideLoader]);

  // --- Mutação para atualizar o Grupo ---
  const updateGroupMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.groups.update(id),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        resetForm();
        navigate("/almoxarifado/produtos/grupos");
        queryClient.invalidateQueries({
          queryKey: ["almox_groups"],
        });
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedGroup = cleanPayload(formData);

      const typesIds = (formData.group?.types || []).map((t) =>
        t.value !== undefined ? t.value : t,
      );

      const payload = {
        group: {
          ...cleanedGroup.group,
          types: typesIds,
        },
      };
      await updateGroupMutation.mutateAsync(payload);
    } catch (error) {
      console.log("Erro ao enviar grupo:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateGroupMutation]);

  const handleBack = useCallback(() => {
    navigate("/almoxarifado/produtos/grupos");
  }, [navigate]);

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const renderForm = () => {
    const renderedSections = new Set();

    return groupFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={(fieldId) => selects[fieldId]?.options || []}
            getSelectedValue={(fieldId) =>
              selects[fieldId]?.selectedValue || null
            }
            getSelectMeta={getSelectMeta}
          />
        </div>
      );
    });
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Grupo"
        showBackButton={true}
        backUrl="/almoxarifado/produtos/grupos"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Salvar Alterações"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
          isLoading={isLoadingGroup || updateGroupMutation.isPending}>
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditGroupPage;
